import React, { useCallback, useContext, useMemo } from "react";
import { API } from "../api/Api";
import { useFeatureFlag } from "../common/contexts/UseFeatureFlags";
import FullDetail from "../common/panels/FullDetail/FullDetail";
import { DatasetContext, FullScreenOpts } from "./Dataset";
import { Details } from "./tiles/Details/Details";
import { Files } from "./tiles/Files/Files";
import { Parameters } from "./tiles/Parameters/Parameters";
import { SourceInformation } from "./tiles/SourceInformation/SourceInformation";
import { Viewer } from "./tiles/Viewer/Viewer";
import { ViewerFullscreen } from "./tiles/Viewer/ViewerFullscreen";
import { SharedContentTile } from "../common/sidebar/tiles/SharedContentTile";
import { Alert } from "../common/overlays/Alert/Alert";
import { PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { CustomResizeHandle } from "../common/panels/misc/CustomResizeHandle";
import { CustomFieldReferencesTile } from "../common/sidebar/tiles/CustomFieldReferencesTile";
import { EntitySidebarTileAutosorter } from "../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTileAutosorter";
import { Dataset } from "../api/Datasets";

interface Props {
  api: API;
  entity: Dataset;
  viewViewer: boolean;
}
export const DatasetDetail = ({ api, entity, viewViewer }: Props) => {
  const context = useContext(DatasetContext);

  const fullscreen = useMemo(() => context?.fullscreen.get, [context?.fullscreen.get]);
  const setFullScreen = useMemo(() => context?.fullscreen.set, [context?.fullscreen.set]);

  const fullScreenToggle = useCallback(
    (component: FullScreenOpts["component"]) => {
      if (fullscreen && setFullScreen) {
        setFullScreen({ ...fullscreen, state: !fullscreen.state, component: component });
      }
    },
    [fullscreen, setFullScreen]
  );

  const isElnActive = useFeatureFlag("eln");
  const containsMestreNova = useMemo(() => entity.format?.id === "multi_mestrelab_mnova", [entity.format?.id]);

  if (entity?.isViewableEntity) return <Alert fit centered type="danger" message="Dataset not found!" />;

  if (viewViewer)
    return (
      <FullDetail.FullBody>
        <ViewerFullscreen dataset={entity} viewShared={context?.viewShared} />
      </FullDetail.FullBody>
    );

  return (
    <FullDetail.Body>
      <FullDetail.Body.Sidebar>
        <Details dataset={entity} />
        {/* {dataset.sample && <Sample api={api} dataset={dataset} />} */}
        {!context?.viewShared ? (
          <EntitySidebarTileAutosorter>
            <SourceInformation dataset={entity} />

            <SharedContentTile
              defaultFilters={{ datasetIds: [entity.id] }}
              excludeFilters={{ datasetIds: () => false }}
            />
            <CustomFieldReferencesTile
              entityType="Dataset"
              entity={entity}
              labNotebookEntryFilters={isElnActive ? { datasetReferenceIds: [entity.id] } : undefined}
            />
          </EntitySidebarTileAutosorter>
        ) : (
          <SourceInformation dataset={entity} />
        )}
      </FullDetail.Body.Sidebar>

      {entity.isDeleted ? (
        <FullDetail.Body.Content.Flex>
          <Files dataset={entity} fullscreen={fullscreen} fullScreenToggle={fullScreenToggle} />
        </FullDetail.Body.Content.Flex>
      ) : (
        fullscreen &&
        !fullscreen.state && (
          <FullDetail.Body.Content>
            <FullDetail.Body.Content.Flex>
              <PanelGroup direction="vertical">
                {entity.parsingState !== "ParsedSuccessfully" || (containsMestreNova && context?.mWebEnabled) ? (
                  <>
                    <FullDetail.Body.Content.Flex
                      {...(containsMestreNova && {
                        style: { border: "1px solid var(--gray-200)" },
                        panelProps: { defaultSize: 80 },
                      })}
                    >
                      <Viewer api={api} dataset={entity} fullScreenToggle={fullScreenToggle} />
                    </FullDetail.Body.Content.Flex>
                    <PanelResizeHandle />
                    <FullDetail.Body.Content.Flex>
                      <Files dataset={entity} fullscreen={fullscreen} fullScreenToggle={fullScreenToggle} />
                    </FullDetail.Body.Content.Flex>
                  </>
                ) : (
                  <>
                    {entity.parsedMetadata.tracks && entity.parsedMetadata.parameters && (
                      <>
                        <FullDetail.Body.Content.Flex>
                          <Viewer api={api} dataset={entity} fullScreenToggle={fullScreenToggle} />
                        </FullDetail.Body.Content.Flex>
                        <CustomResizeHandle orientation="vertical" />
                        <FullDetail.Body.Content.Flex>
                          <PanelGroup direction="horizontal">
                            <FullDetail.Body.Content.Flex>
                              <Parameters
                                api={api}
                                dataset={entity}
                                fullscreen={fullscreen}
                                fullScreenToggle={fullScreenToggle}
                              />
                            </FullDetail.Body.Content.Flex>
                            <CustomResizeHandle orientation="horizontal" />
                            <FullDetail.Body.Content.Flex>
                              <Files dataset={entity} fullscreen={fullscreen} fullScreenToggle={fullScreenToggle} />
                            </FullDetail.Body.Content.Flex>
                          </PanelGroup>
                        </FullDetail.Body.Content.Flex>
                      </>
                    )}
                    {entity.parsedMetadata.tracks && !entity.parsedMetadata.parameters && (
                      <>
                        <FullDetail.Body.Content.Flex>
                          <Viewer api={api} dataset={entity} fullScreenToggle={fullScreenToggle} />
                        </FullDetail.Body.Content.Flex>
                        <PanelResizeHandle />
                        <FullDetail.Body.Content.Flex>
                          <Files dataset={entity} fullscreen={fullscreen} fullScreenToggle={fullScreenToggle} />
                        </FullDetail.Body.Content.Flex>
                      </>
                    )}
                    {!entity.parsedMetadata.tracks && entity.parsedMetadata.parameters && (
                      <>
                        <FullDetail.Body.Content.Flex>
                          <Parameters
                            api={api}
                            dataset={entity}
                            fullscreen={fullscreen}
                            fullScreenToggle={fullScreenToggle}
                          />
                        </FullDetail.Body.Content.Flex>
                        <PanelResizeHandle />
                        <FullDetail.Body.Content.Flex>
                          <Files dataset={entity} fullscreen={fullscreen} fullScreenToggle={fullScreenToggle} />
                        </FullDetail.Body.Content.Flex>
                      </>
                    )}
                    {!entity.parsedMetadata.tracks && !entity.parsedMetadata.parameters && (
                      <FullDetail.Body.Content.Flex>
                        <Files dataset={entity} fullscreen={fullscreen} fullScreenToggle={fullScreenToggle} />
                      </FullDetail.Body.Content.Flex>
                    )}
                  </>
                )}
              </PanelGroup>
            </FullDetail.Body.Content.Flex>
          </FullDetail.Body.Content>
        )
      )}
      {fullscreen && fullscreen.state && fullscreen.component === "files" && (
        <FullDetail.Body.Content>
          <Files dataset={entity} fullscreen={fullscreen} fullScreenToggle={fullScreenToggle} />
        </FullDetail.Body.Content>
      )}
      {fullscreen && fullscreen.state && fullscreen.component === "parameters" && (
        <FullDetail.Body.Content>
          <Parameters api={api} dataset={entity} fullscreen={fullscreen} fullScreenToggle={fullScreenToggle} />
        </FullDetail.Body.Content>
      )}
    </FullDetail.Body>
  );
};
